import { useEffect, useState } from 'react';
import { DataTypesEnum, ObservableLevelEnumValue } from '../../data/enums';
import {
  SingleLineTextField,
  SingleLineValidation,
} from './SingleLineTextField';
import DropdownField, { DropdownFieldValidation } from './DropdownField';
import { observationService } from '../../services/observationService';
import { cacheInvalidationService } from '../../services/cacheInvalidationService';
import { Label } from './Label';
import { inspectionDB } from '../../data/database.structure';
import {
  ActionStatusBarModel,
  ActionTypeStore,
} from '../../store/inspection/observation/actionTypeStore';
import WearDisplayHelpers from '../../helpers/wearDisplayHelpers';
import { PartDimensionModel } from '../../data/types';

interface TapeMeasureProps {
  measurementPath: string;
  shoesPath: string;
  extensionPath: string;
  numberOfShoes: number;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  inspectionCategoryTypeId: string;
}

const TapeMeasure = ({
  measurementPath,
  shoesPath,
  extensionPath,
  numberOfShoes,
  entityId,
  entityExternalId,
  dataLevel,
  inspectionCategoryTypeId,
  inspectionId,
}: TapeMeasureProps) => {
  const [measurement, setMeasurement] = useState('');
  const [shoes, setShoes] = useState('');
  const [extension, setExtension] = useState('No measurement');
  const [rangeError, setRangeError] = useState<string | null>(null);
  // DISPLAY-ONLY (#6032): baseline/limit for showing wear extension instead of
  // the raw calculated value. Stored observation values stay RAW.
  const [partDimension, setPartDimension] = useState<
    PartDimensionModel | undefined
  >(undefined);

  const [measurementValidation, setMeasurementValidation] =
    useState<SingleLineValidation>(null);
  const [shoesValidation, setShoesValidation] =
    useState<DropdownFieldValidation>(null);

  // Load the part dimension (baseline/limit) for the calculated tape pitch extension
  useEffect(() => {
    const loadPartDimension = async () => {
      const observableItem = await inspectionDB.observableItems
        .where('identifier')
        .equals(extensionPath)
        .first();
      if (!observableItem) return;

      const dimension = await new ActionTypeStore().getPartDimension({
        equipmentPartId: entityExternalId,
        observableItemId: observableItem.id,
      });
      setPartDimension(dimension);
    };

    loadPartDimension();
  }, []);

  const setShoesValidationError = (error: DropdownFieldValidation) => {
    setShoesValidation(error);
    setTimeout(() => {
      setShoesValidation(null);
    }, 1500);
  };

  const handleMeasurementChange = async (value: string) => {
    if (shoes === '') {
      // if shoes is empty, set Measurement
      setMeasurement(value);
      return;
    }

    const shoesNum = parseInt(shoes);
    if (isNaN(shoesNum) || shoesNum <= 0) {
      setShoes('');
      await deleteObservation({ path: shoesPath });
      calculateExtension(value, '');
      return;
    }

    const measurementNum = parseFloat(value);
    if (isNaN(measurementNum)) {
      await deleteObservation({ path: measurementPath });
      setMeasurement('');
      calculateExtension('', shoes);
      return;
    }

    const valid = await validate({
      measurement: measurementNum,
      numShoes: shoesNum,
    });
    if (!valid.valid) {
      await deleteObservation({ path: measurementPath });
      if (
        valid.actionStatus &&
        (valid.actionStatus.percentage < 0 ||
          valid.actionStatus.percentage > 250)
      ) {
        setMeasurement('');
        calculateExtension('', shoes);
        setMeasurementValidation('BelowOrEqualZero');
        setTimeout(() => {
          setMeasurementValidation('');
        }, 1500);
        calculateExtension('', shoes);
        // #6032: never reject silently — tell the inspector what range applies
        // (min/max are per-pitch, so scale by the number of shoes measured over)
        setRangeError(
          WearDisplayHelpers.formatOutOfRangeError({
            value: measurementNum,
            minValue: valid.actionStatus.minValue * shoesNum,
            maxValue: valid.actionStatus.maxValue * shoesNum,
          }),
        );
        return;
      }

      calculateExtension(value, shoes);

      return;
    }

    setRangeError(null);
    setMeasurement(value);
    calculateExtension(value, shoes);
  };

  const handleShoesChange = async (value: string) => {
    if (measurement === '') {
      // if measurement is empty, set Shoes
      setShoes(value);
      return;
    }

    const measurementNum = parseFloat(measurement);
    if (isNaN(measurementNum)) {
      calculateExtension('', value);
      setMeasurementValidation('InvalidInput');
      setTimeout(() => {
        setMeasurementValidation('');
      }, 1500);
      return;
    }

    const shoesNum = parseInt(value);
    if (isNaN(shoesNum) || shoesNum <= 0) {
      setShoes('');
      setShoesValidationError('InvalidInput');

      await deleteObservation({ path: shoesPath });
      calculateExtension(measurement, '');

      return;
    }

    const valid = await validate({
      measurement: measurementNum,
      numShoes: shoesNum,
    });
    if (!valid.valid) {
      await deleteObservation({ path: shoesPath });
      setShoes('');
      setShoesValidationError('InvalidInput');
      calculateExtension(measurement, '');
      // #6032: never reject silently — tell the inspector what range applies
      if (valid.actionStatus) {
        setRangeError(
          WearDisplayHelpers.formatOutOfRangeError({
            value: measurementNum,
            minValue: valid.actionStatus.minValue * shoesNum,
            maxValue: valid.actionStatus.maxValue * shoesNum,
          }),
        );
      }
      return;
    }

    setRangeError(null);
    setShoes(value);

    calculateExtension(measurement, value);
  };

  const deleteObservation = async ({ path }: { path: string }) => {
    // Delete calcualted extension
    await observationService.deleteObservationsByProps({
      dataLevel: dataLevel,
      entityId: entityId,
      entityExternalId: entityExternalId,
      inspectionCategoryId: inspectionCategoryTypeId,
      inspectionId: inspectionId,
      path: extensionPath,
    });

    // Delete the observation
    await observationService.deleteObservationsByProps({
      dataLevel: dataLevel,
      entityId: entityId,
      entityExternalId: entityExternalId,
      inspectionCategoryId: inspectionCategoryTypeId,
      inspectionId: inspectionId,
      path: path,
    });

    // Direct observationService writes bypass the mutation hooks' invalidation;
    // without this the Action Status bar keeps stale observations (#10549).
    await cacheInvalidationService.invalidateInspectionAfterObservationChangeImmediate(
      inspectionId,
    );
  };

  const getCalculation = ({
    measurement,
    numShoes,
  }: {
    measurement: number;
    numShoes: number;
  }): number => {
    const result = measurement / numShoes;
    return result;
  };

  async function validate({
    measurement,
    numShoes,
  }: {
    measurement: number;
    numShoes: number;
  }): Promise<{
    actionStatus: ActionStatusBarModel | undefined;
    valid: boolean;
  }> {
    const calculation = getCalculation({ measurement, numShoes });

    const observatbleItem = await inspectionDB.observableItems
      .where('identifier')
      .equals(extensionPath)
      .first();
    if (!observatbleItem) {
      console.error('Observable item not found');
      return { actionStatus: undefined, valid: false };
    }

    const actionTypeStore = new ActionTypeStore();
    const actionStatus = await actionTypeStore.getPartActionStatus({
      value: calculation,
      equipmentPartId: entityExternalId,
      observableItemId: observatbleItem.id,
    });

    const response = ActionTypeStore.validateActionStatus({
      actionStatus: actionStatus,
      multiplier: numShoes,
    });

    return { actionStatus, valid: response };
  }

  const calculateExtension = async (meas: string, numShoes: string) => {
    if (meas && numShoes) {
      const measNum = parseFloat(meas);
      const shoesNum = parseInt(numShoes);
      if (!isNaN(measNum) && !isNaN(shoesNum) && shoesNum > 0) {
        const result = getCalculation({
          measurement: measNum,
          numShoes: shoesNum,
        }).toFixed(2);
        // if (measNum / shoesNum === 0) {
        //   setShoes('');
        //   setMeasurement(''); // clear the value if value is below 0
        //   setError('BelowOrEqualZero');
        //   setTimeout(() => {
        //     setError('');
        //   }, 1500);
        // }
        setExtension(result);

        await observationService.upsertObservation({
          dataLevel: dataLevel,
          entityId: entityId,
          entityExternalId: entityExternalId,
          inspectionCategoryId: inspectionCategoryTypeId,
          inspectionId: inspectionId,
          path: extensionPath,
          value: result,
        });
        // The adjacent field's draft flush also invalidates, but it races this
        // direct write (setTimeout(0) vs awaited upsert) — invalidate explicitly
        // so the Action Status bar always sees the new extension (#10549).
        await cacheInvalidationService.invalidateInspectionAfterObservationChangeImmediate(
          inspectionId,
        );
      } else {
        setExtension('Invalid input');
        await observationService.deleteObservationsByProps({
          dataLevel: dataLevel,
          entityId: entityId,
          entityExternalId: entityExternalId,
          inspectionCategoryId: inspectionCategoryTypeId,
          inspectionId: inspectionId,
          path: extensionPath,
        });
        await cacheInvalidationService.invalidateInspectionAfterObservationChangeImmediate(
          inspectionId,
        );
      }
    } else {
      setExtension('No measurement');
    }
  };

  // Load Default
  useEffect(() => {
    const getDefaultValue = async () => {
      const observation = await observationService.getObservation({
        dataLevel: dataLevel,
        entityId: entityId,
        entityExternalId: entityExternalId,
        inspectionId: inspectionId,
        inspectionCategoryId: inspectionCategoryTypeId,
        path: extensionPath,
      });

      if (observation) {
        const value = observationService.convertValue<number>(
          observation.value,
          DataTypesEnum.number,
        );
        setExtension(value.toFixed(2));
        // onDefaultLoad && onDefaultLoad(value);
      }
    };

    getDefaultValue();
  }, []);

  // DISPLAY-ONLY (#6032): show wear extension (calculated value − baseline)
  // instead of the raw calculated value. The stored
  // CalculatedTapePitchExtension value stays RAW.
  const extensionNum = parseFloat(extension);
  const hasExtension = !isNaN(extensionNum);

  return (
    <div className="py-4">
      <div>
        <Label tooltipKey="undercarriage-shoes-pitchextension-tapemeasure">
          Measured pitch (tape measure)
        </Label>
      </div>

      <div className="space-y-4">
        <div className="flex space-x-4">
          <div className="flex-1">
            <SingleLineTextField
              path={measurementPath}
              entityId={entityId}
              entityExternalId={entityExternalId}
              dataLevel={dataLevel}
              inspectionId={inspectionId}
              inspectionCategoryTypeId={inspectionCategoryTypeId}
              onChange={(value) => {
                handleMeasurementChange(value);
              }}
              onDefaultLoad={(value) => {
                setMeasurement(value);
              }}
              validationError={measurementValidation}
              placeholder="Measurement (mm)"
            />
          </div>

          <div className="px-1"></div>

          <div className="flex-1">
            <div className="pt-2"></div>
            <DropdownField
              path={shoesPath}
              entityId={entityId}
              entityExternalId={entityExternalId}
              dataLevel={dataLevel}
              inspectionId={inspectionId}
              inspectionCategoryTypeId={inspectionCategoryTypeId}
              options={Array.from({ length: numberOfShoes }, (_, i) =>
                (i + 1).toString(),
              )}
              placeholder="Number of shoes"
              onChange={(value) => {
                handleShoesChange(value);
              }}
              onDefaultLoad={(value) => {
                setShoes(value);
              }}
              value={shoes.toString()}
              validationError={shoesValidation}
            />
          </div>
        </div>

        {rangeError && <p className="text-sm text-red-500">{rangeError}</p>}

        <div>
          <h3 className="mb-2 text-sm text-primaryColor">
            Calculated tape pitch extension
          </h3>
          <div className="py-2">
            {hasExtension
              ? WearDisplayHelpers.formatExtensionDisplayValue({
                  average: extensionNum,
                  dimension: partDimension,
                }) + 'mm'
              : extension}
          </div>
          {hasExtension && (
            <div className="text-sm whitespace-pre-line text-inkMuted">
              {WearDisplayHelpers.formatExtensionLabel({
                average: extensionNum,
                dimension: partDimension,
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TapeMeasure;
